import React from 'react';
import type { Breakpoint } from '../../hooks/useBreakpoint';

interface LoginPanelProps {
  mode: 'enterprise' | 'mssp';
  onToggle: () => void;
  onSignIn: () => void;
  isLoading: boolean;
  error: string | null;
  breakpoint: Breakpoint;
}

const LoginPanel: React.FC<LoginPanelProps> = ({ mode, onToggle, onSignIn, isLoading, error, breakpoint }) => {
  const isEnterprise = mode === 'enterprise';
  const isStacked = breakpoint === 'mobile' || breakpoint === 'tablet';
  const isMobile = breakpoint === 'mobile';

  const padding = isMobile ? '32px 24px' : isStacked ? '24px 32px' : '48px 36px';
  const borderClass = isStacked ? 'border-b border-white/[0.04]' : 'border-l border-white/[0.04]';
  const animation = isStacked ? 'fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s both' : 'slideIn 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s both';
  const logoSize = isMobile ? 72 : 100;

  return (
    <div
      className={`flex flex-col justify-center items-center ${borderClass}`}
      style={{
        padding,
        background: 'rgba(13,11,45,0.85)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        animation,
      }}
    >
      <img
        src="/anchor-mark-96.png"
        alt="Data Anchor"
        className="object-contain mb-6"
        style={{ width: logoSize, height: logoSize, filter: 'drop-shadow(0 0 28px rgba(69,69,255,0.3))' }}
      />

      <h2 className="font-mono text-lg font-semibold tracking-[0.15em] uppercase text-center mb-1">Data<span className="text-anchor-teal">_</span>Anchor</h2>

      <p className="font-mono text-[10px] text-white/60 tracking-[0.18em] uppercase text-center mb-9 transition-opacity duration-300">
        {isEnterprise ? 'Continuous security assessment' : 'Every client, continuously assessed'}
      </p>

      {/* Toggle pill */}
      <div className="flex items-center mb-7">
        <div
          className="relative w-[220px] h-11 bg-white/[0.06] border border-white/10 rounded-[22px] cursor-pointer overflow-hidden flex items-center"
          onClick={onToggle}
        >
          <div
            className="absolute top-[3px] left-[3px] h-[38px] bg-anchor-blue rounded-[19px] transition-transform duration-350"
            style={{
              width: 'calc(50% - 3px)',
              transform: isEnterprise ? 'translateX(0)' : 'translateX(100%)',
              transitionTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
            }}
          />
          <span className={`flex-1 text-center text-[11px] font-medium tracking-[0.06em] relative z-10 select-none transition-colors duration-300 ${isEnterprise ? 'text-white' : 'text-white/40'}`}>
            Enterprise
          </span>
          <span className={`flex-1 text-center text-[11px] font-medium tracking-[0.06em] relative z-10 select-none transition-colors duration-300 ${!isEnterprise ? 'text-white' : 'text-white/40'}`}>
            MSSP
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="w-10 h-px mb-9" style={{ background: 'linear-gradient(90deg, transparent, rgba(69,69,255,0.3), transparent)' }} />

      {/* Error message */}
      {error && (
        <div className="w-full max-w-[280px] mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-xs text-red-300 text-center">{error}</p>
        </div>
      )}

      {/* Sign-in button */}
      <button
        onClick={onSignIn}
        disabled={isLoading}
        className="w-full max-w-[280px] py-3.5 px-5 bg-anchor-blue border-none rounded-lg text-white text-[13px] cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 hover:bg-[#3636DD] hover:-translate-y-px hover:shadow-[0_8px_28px_rgba(69,69,255,0.35)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-[18px] w-[18px] text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Signing in...
          </>
        ) : (
          <>
            <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Sign in with Microsoft
          </>
        )}
      </button>

      {!isStacked && (
        <div className="mt-auto pt-10 text-[10px] text-white/[0.15] tracking-[0.08em] text-center">
          Powered by Whalen IT Solutions
        </div>
      )}
    </div>
  );
};

export default LoginPanel;
